<script setup lang="ts">
import { useRoute } from 'vue-router';
import { computed, onMounted, watch } from 'vue';
import { IonButton, IonContent, IonIcon, IonSpinner } from '@ionic/vue';
import { closeOutline, trendingUpOutline } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';

import { useCart } from '@/modules/shop/composables/useCart';
import { GenericProduct } from '@/shared/interfaces/genericProduct';
import useQueue from '@/modules/queue/composables/useQueue';
import { useCheckout } from '@/shared/composables/useCheckout';
import { useCartStore } from '@/stores/shop/cartStore';

// Init
const { t } = useI18n();
const route = useRoute();
const slotId = +route.params.slotId;
const activityId = +route.query.activityId;

const { getQueueActivity, getMyQueues } = useQueue();
const { activity, isFetchedActivity } = getQueueActivity(activityId);
const { myQueues, isFetchingMyQueues } = getMyQueues();
const { addToCart, getCartItems, getCartTotalItemsByCategory } = useCart();
const { processCheckout, handleCheckout: executeCheckout } = useCheckout();

// Find the slot being upgraded
const currentSlot = computed(() => {
	return myQueues.value?.find(s => s.id === slotId);
});

// Create upsell product that matches the expected interface
const upsellProduct = computed(() => {
	if (!activity.value || !activity.value.upsell_price || !activity.value.upsell_currency_id) {
		return null;
	}

	return new GenericProduct({
		id: activityId * 10000 + slotId, // Generate unique ID for upsell
		name: t('queue.upgrade_to_fastpass'),
		description: `${t('queue.upgrade_from_normal_to_fastpass')}`,
		price: activity.value.upsell_price / 100, // Convert from cents
		currency_id: activity.value.upsell_currency_id,
		currency: activity.value.upsellCurrency || {
			id: activity.value.upsell_currency_id,
			prefix: '€',
			suffix: '',
			exchange_rate: 1
		},
		vat_percentage: 0,
		order: 1
	});
});

// Track if item has been added
const itemAddedToCart = computed(() => getCartTotalItemsByCategory('queue_upsell'));

// Clear upsell items from cart on mount
onMounted(() => {
	const cartStore = useCartStore();
	// Remove only queue_upsell items from cart
	cartStore.cart = cartStore.cart.filter((item: any) => item.category !== 'queue_upsell');

	console.log('Cart cleared, cart size:', cartStore.cart.length);
});

// Function to add item to cart
const addItemToCart = () => {
	if (upsellProduct.value && isFetchedActivity.value && myQueues.value && itemAddedToCart.value === 0) {
		console.log('Adding upsell to cart:', upsellProduct.value);
		addToCart(upsellProduct.value, 'queue_upsell', {
			slotId: slotId,
			activityId: activityId,
		});
		console.log('Cart after add:', getCartTotalItemsByCategory('queue_upsell'));
	}
};

// Watch for all dependencies and trigger add
watch([upsellProduct, isFetchedActivity, () => myQueues.value], () => {
	console.log('Watch triggered:', {
		hasProduct: !!upsellProduct.value,
		fetched: isFetchedActivity.value,
		hasQueues: !!myQueues.value,
		cartItems: itemAddedToCart.value
	});

	addItemToCart();
});

// Also try to add on mount (with a slight delay to ensure data is loaded)
onMounted(async () => {
	// Wait a bit for data to load
	setTimeout(() => {
		console.log('Mount timeout - attempting to add to cart');
		addItemToCart();
	}, 100);
});

// Handle checkout
const handleCheckout = async () => {
	// Prepare the transaction data (sets up the store)
	await processCheckout('QUEUE_UPSELL', {
		slotId,
		activityId,
	});

	// Execute the actual checkout API call which will handle redirect
	await executeCheckout();
};
</script>

<template>
	<ion-content>
		<div class="flex flex-col justify-between px-[34px]">
			<div class="flex flex-col mt-12 mb-10 items-center">
				<div class="flex items-center justify-center mb-4">
					<ion-button
						class="rounded ion-no-padding ion-no-margin w-[60px] h-[60px] hover:rounded-full hover:bg-white/20"
						:routerLink="`/my-queues`">
						<ion-icon class="text-[38px] text-white cursor-pointer" :icon="closeOutline"></ion-icon>
					</ion-button>
				</div>
				<h1 class="font-sourcesanspro-semibold text-lg2">{{ activity?.name }}</h1>
			</div>

			<div v-if="!isFetchedActivity || isFetchingMyQueues" class="flex items-center justify-center mb-4">
				<ion-spinner name="dots"></ion-spinner>
			</div>

			<div v-if="activity && currentSlot && isFetchedActivity && upsellProduct">
				<!-- Current Status -->
				<div class="mb-6">
					<div class="bg-white/10 rounded-lg p-4">
						<p class="text-white/70 text-sm mb-2">{{ t('queue.current_ticket') }}</p>
						<div class="flex items-center justify-between">
							<div>
								<p class="text-white font-sourcesanspro-semibold text-base">{{ t('queue.regular_pass') }}</p>
								<p class="text-white/70 text-sm">{{ t('queue.position') }}: #{{ currentSlot.position_number }}</p>
							</div>
							<div class="text-right">
								<p class="text-white/70 text-sm">{{ t('queue.estimated_wait_time') }}</p>
								<p class="text-white font-sourcesanspro-semibold">{{ currentSlot.estimated_wait_minutes || 0 }} min</p>
							</div>
						</div>
					</div>
				</div>

				<!-- Upgrade Option -->
				<div class="mb-8">
					<div class="flex items-center mb-4">
						<ion-icon class="mr-2 text-[20px] text-white cursor-pointer" :icon="trendingUpOutline"></ion-icon>
						<h2 class="font-sourcesanspro-semibold text-lg inline">{{ t('queue.upgrade_option') }}</h2>
					</div>

					<div class="bg-gradient-to-r from-[#f9af21]/20 to-[#f9af21]/10 border-2 border-[#f9af21] rounded-lg p-4">
						<div class="flex items-center justify-between mb-3">
							<div>
								<p class="text-white font-sourcesanspro-semibold text-lg">⭐️ {{ t('queue.priority_pass') }} ⭐️</p>
								<p class="text-white/80 text-sm">{{ upsellProduct.description }}</p>
							</div>
						</div>
						<div class="flex items-center justify-between">
							<p class="text-white/70 text-sm">{{ t('queue.upgrade_price') }}</p>
							<p class="text-[#f9af21] font-sourcesanspro-bold text-xl">
								{{ upsellProduct.currency.prefix }}{{ upsellProduct.price.toFixed(2) }}{{ upsellProduct.currency.suffix }}
							</p>
						</div>
					</div>
				</div>

				<!-- Benefits -->
				<div class="mb-8">
					<h3 class="text-white font-sourcesanspro-semibold text-base mb-3">{{ t('queue.fastpass_benefits') }}</h3>
					<ul class="space-y-2">
						<li class="flex items-start text-white/80 text-sm">
							<span class="text-[#f9af21] mr-2">✓</span>
							<span>{{ t('queue.benefit_skip_line') }}</span>
						</li>
						<li class="flex items-start text-white/80 text-sm">
							<span class="text-[#f9af21] mr-2">✓</span>
							<span>{{ t('queue.benefit_priority_access') }}</span>
						</li>
						<li class="flex items-start text-white/80 text-sm">
							<span class="text-[#f9af21] mr-2">✓</span>
							<span>{{ t('queue.benefit_reduced_wait') }}</span>
						</li>
					</ul>
				</div>
			</div>

			<div v-else-if="activity && isFetchedActivity && !upsellProduct" class="mb-8 text-center">
				<p class="text-white/70">{{ t('queue.upsell_not_available') }}</p>
			</div>

			<div class="mb-8">
				<ion-button
					v-if="isFetchedActivity && !isFetchingMyQueues"
					:disabled="itemAddedToCart === 0"
					class="w-full hover:rounded-full hover:bg-white"
					expand="full"
					size="large"
					shape="round"
					@click="handleCheckout()">
					<div class="flex w-full items-center justify-center">
						<span class="text-m2">{{ t('queue.confirm_upgrade') }}</span>
					</div>
				</ion-button>
			</div>
		</div>
	</ion-content>
</template>

<style lang="scss" scoped>
ion-content {
	--background: #0a0a0a;
}
</style>
